import { Fragment, useEffect, useState } from "react";
import { Badge, Button, Collapse, Flex, Form, Input, Popconfirm, Spin, message } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { CheckOutlined, CloseOutlined, QuestionCircleOutlined, } from '@ant-design/icons';
import { mdlReservation } from "../../models/domain/Reservation";
import { PdfIcon } from "../common/CustomIcons";
import { SFModal } from "../elements/SFModal";
import { enmPdfRendererType } from "../../models/enums/PdfRenderer";
import { useAppDispatch, useAppSelector } from "../../store/Hooks";
import { mdlPDFRendererProps } from "../../models/ui-models/PDFRendererProps";
import { enmExportStatus } from "../../models/enums/ExportStatus";
import _ from 'lodash';
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import { ReservationService } from "../../services/ReservationService";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { mdlReservationRejectRequest } from "../../models/service-models/reservation/ReservationRejectRequest";
import { enmConfirmationStatus } from "../../models/enums/ConfirmationStatus";
import { setPdfExportObject } from "../../store/SiteSlice";
import { enmPdfExportType } from "../../models/enums/PdfExportType";
import { NotConfirmSelect } from "../common/NotConfirmSelect";
import { HotelNotConfirmSelect } from "../common/HotelNotConfirmSelect";
import { mdlReservationPrintDetail } from "../../models/ui-models/ReservationPrintDetail";

export type ReservationRejectProps = {
    reservations?: mdlReservation[];
    closeCallBack?: Function;
    open?: boolean;
    callback?: Function;
    displayMode?: boolean;
    fromEmail?: boolean;
}

export const ReservationReject = (props: ReservationRejectProps) => {
    const { t } = useTranslation();
    const [openSameNoteModal, setOpenSameNoteModal] = useState<boolean>(false);
    const [rejectReservations, setRejectReservations] = useState<Array<mdlReservation>>();
    const [allNote, setAllNote] = useState<string | undefined>();
    const dispatch = useAppDispatch();
    const exportStatus = useAppSelector(x => x.site.exportStatus);
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        setRejectReservations(props.reservations);
    }, [props]);



    //#region EXPORT HANDLERS
    const exportToPDF = (type: enmPdfExportType, reservations: Array<mdlReservation>) => {
        var exportProps: mdlPDFRendererProps = {
            params: { reservations: reservations },
            rendererType: enmPdfRendererType.ReservationVoucher,
            type: type
        }
        dispatch(setPdfExportObject(exportProps));
    }

    const handleExportToPdfClick = (reservation: mdlReservation, index: number) => {
        exportToPDF(enmPdfExportType.PDF, [reservation]);
    }

    const handleExportToPDFALLClick = async () => {
        if (rejectReservations) {
            getMultiResDetail({
                request: {
                    reservations: rejectReservations!.map(h => Object.assign(h!, {
                        reservationId: h.reservationId!,
                        agencyId: h.agencyId!,
                        paxId: h.paximumId!
                    }))
                }
                , callbackFn: (reservations?: Array<mdlReservation>) => exportToPDF(enmPdfExportType.PDF, reservations!)
            });
        }
        else {
            toast.error(t("common.noReservationSelected"));
        }
    }

    const handlePrintAfterSuccess = () => {
        var cloneReservations = _.cloneDeep(rejectReservations);
        _.each(cloneReservations, function (res) {
            res.confirmationStatus = enmConfirmationStatus.No
        })
        if (cloneReservations) {
            exportToPDF(enmPdfExportType.PDF, cloneReservations)
            props.callback && props.callback();
        }
    }
    //#endregion

    //#region NOTE HANDLERS
    const handleRemoveAllNotes = () => {
        if (rejectReservations) {
            setRejectReservations(prevRejectReservations =>
                prevRejectReservations?.map(rejectReservation =>
                ({
                    ...rejectReservation, confirmeNote: undefined
                })
                )
            );
        }
    }

    const handleAllNoteConfirm = () => {
        if (rejectReservations && allNote) {
            setRejectReservations(prevRejectReservations =>
                prevRejectReservations?.map(rejectReservation =>
                ({
                    ...rejectReservation, confirmeNote: allNote
                })
                )
            );
            setOpenSameNoteModal(false);
            messageApi.open({
                type: "success",
                content: t("resReject.allNotesDone"),
                duration: 1000,
            });
        }
    }

    const handleNoteChange = (value: string, index: number, toAll: boolean = false) => {
        if (toAll) {
            setAllNote(value);
        } else {
            if (rejectReservations) {
                setRejectReservations(prevRejectReservations =>
                    prevRejectReservations?.map((rejectReservation, resIndex) =>
                        index === resIndex
                            ? {
                                ...rejectReservation,
                                confirmeNote: value
                            }
                            : rejectReservation
                    )
                );
            }
            else
                toast.error(t("resReject.reservationNoteArrayError"));
        }
    }


    const handleNotConfirmMessageSelect = (value: string[], option: { label: string, value: string }[], index: number) => {
        var seperator = " --- ";
        var messages = option.map(x => x.label);
        var note = messages.join(seperator);
        if (rejectReservations) {
            setRejectReservations(prevRejectReservations =>
                prevRejectReservations?.map((rejectReservation, resIndex) =>
                    index === resIndex
                        ? {
                            ...rejectReservation,
                            confirmeNote: note,
                        }
                        : rejectReservation
                )
            );
        }
        else
            toast.error(t("resReject.reservationNoteArrayError"));
    }
    //#endregion

    //#region SEND HANDLERS
    const handleSendClick = () => {
        if (rejectReservations)
            sendRejection({
                request: {
                    reservations: rejectReservations.map(r => ({
                        agencyId: r.agencyId,
                        paximumId: r.paximumId,
                        reservationId: r.reservationId,
                        note: r.confirmeNote
                    })),
                    fromEmail: props.fromEmail
                }, printAll: false
            });
    }

    const handleSendAndExportToPDFClick = () => {
        if (rejectReservations)
            sendRejection({
                request: {
                    reservations: rejectReservations.map(r => ({
                        agencyId: r.agencyId,
                        paximumId: r.paximumId,
                        reservationId: r.reservationId,
                        note: r.confirmeNote
                    })),
                    fromEmail: props.fromEmail
                }, printAll: true
            });
    }
    //#endregion

    //#region API CALLS
    const { mutateAsync: getMultiResDetail, isLoading: resDetailLoading, data: resDetail } = useMutation(
        (request: mdlReservationPrintDetail) => ReservationService.MultiDetail(request.request!),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    if (variables.callbackFn) {
                        variables.callbackFn(data.body?.reservations);
                    }
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const { mutateAsync: sendRejection, isLoading: mutateLoading, data: mutateData } = useMutation(
        (data: { request: mdlReservationRejectRequest, printAll: boolean }) => ReservationService.Reject(data.request),
        {            
            onError: error => {
                toast.error(JSON.stringify(error));
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    if (variables.printAll) {
                        handlePrintAfterSuccess();
                    }
                    else {
                        props.callback && props.callback();
                    }
                }
                else {
                    if (data.header.success && data.body?.errors)
                        toast.error(_.first(data.body.errors));
                    else
                        toast.error(t(ObjectHelper.getErrorMessage(data.header.messages)));
                }
            },
        }
    );
    //#endregion

    return (
        <>
            {
                rejectReservations
                &&
                <Spin spinning={exportStatus != enmExportStatus.DONE || mutateLoading || resDetailLoading}>
                    {contextHolder}
                    <Badge.Ribbon text={t("resReject.badgeTitle")} color={"red"}>
                        {
                            <Fragment>
                                <div className="card card-bordered">
                                    <div className="card-inner">
                                        <h6>
                                            {`${t("resReject.totalReservation")}: ${rejectReservations?.length}`}
                                        </h6>
                                    </div>
                                </div>
                                {
                                    rejectReservations.map((reservation: mdlReservation, index: number) => (
                                        <Fragment key={index}>
                                            <div className="card card-bordered">
                                                <div className="card-inner">
                                                    <div className="team">
                                                        <div className="card-inner">
                                                            <div className="project-head">
                                                                <div>
                                                                </div>
                                                                <div>
                                                                    <Button onClick={() => handleExportToPdfClick(reservation, index)} loading={exportStatus != enmExportStatus.DONE} type="primary" icon={<PdfIcon />} size="small">
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.agency")}</span>
                                                                    <span>{reservation.agencyName}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.hotel")}</span>
                                                                    <span>{reservation.hotelName}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.operator")}</span>
                                                                    <span>{reservation.operator}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.voucher")}</span>
                                                                    <span>{reservation.voucherNo}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.checkIn")}</span>
                                                                    <span>{dayjs(reservation.checkIn).format("DD.MM.YYYY")}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.checkOut")}</span>
                                                                    <span>{dayjs(reservation.checkOut).format("DD.MM.YYYY")}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.accomStyle")}</span>
                                                                    <span>{reservation.roomName}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.roomCount")}</span>
                                                                    <span>{reservation.roomCount}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.roomType")}</span>
                                                                    <span>{reservation.roomTypeName}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.totalPax")}</span>
                                                                    <span>{reservation.pax}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resReject.hotelConfirmation")}</span>
                                                                    <span>-</span>
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-sm-6 col-md-4 col-lg-4">
                                                                    <span
                                                                        className="sub-text">{t("resConfirm.price")}</span>
                                                                    {
                                                                        reservation.soldPrice ?
                                                                            <span>{`${reservation.soldPrice} ${reservation.soldCurrency}`}</span>
                                                                            :
                                                                            <>-</>
                                                                    }

                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-sm-12 col-md-12 col-lg-12">
                                                                    <Collapse
                                                                        defaultActiveKey={99}
                                                                        items={[{
                                                                            key: '1',
                                                                            label: t("resReject.noteAndReason"),
                                                                            children:
                                                                                <>
                                                                                    <Flex gap="small" vertical>
                                                                                        {
                                                                                            !props.displayMode
                                                                                            &&
                                                                                            <div className="col-4">
                                                                                                <Flex vertical>
                                                                                                    <label
                                                                                                        className="ant-form-item-label">{t("resReject.reason")}</label>
                                                                                                    <HotelNotConfirmSelect
                                                                                                        hotelId={reservation.hotelId}
                                                                                                        mode="multiple"
                                                                                                        onChange={(value: string[], option: {
                                                                                                            label: string,
                                                                                                            value: string
                                                                                                        }[]) => handleNotConfirmMessageSelect(value, option, index)} />
                                                                                                </Flex>
                                                                                            </div>
                                                                                        }
                                                                                        <div>
                                                                                            <Flex vertical>
                                                                                                <label
                                                                                                    className="ant-form-item-label">{t("resReject.note")}</label>
                                                                                                <Input.TextArea rows={5}
                                                                                                    value={reservation.confirmeNote}
                                                                                                    maxLength={500}
                                                                                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleNoteChange(e.currentTarget.value, index)} />
                                                                                            </Flex>
                                                                                        </div>
                                                                                    </Flex>
                                                                                </>
                                                                        }]}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Fragment>
                                    ))
                                }
                                <div className="card">
                                    <Flex gap="small" wrap justify="space-between">
                                        {
                                            props.reservations && props.reservations.length > 1
                                            &&
                                            <Flex gap="small" wrap justify="flex-start">
                                                <Button color="primary" size="middle" onClick={() => setOpenSameNoteModal(!openSameNoteModal)} variant="outlined">
                                                    {t("resReject.addSameNoteToAll")}
                                                </Button>
                                                <Popconfirm
                                                    title={t("common.deleteWarning")}
                                                    description={t("resReject.removeAllNotesDescription")}
                                                    okText={t("common.okConfirm")}
                                                    cancelText={t("common.cancelConfirm")}
                                                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                    onConfirm={handleRemoveAllNotes}
                                                >
                                                    <Button color="primary" size="middle" variant="outlined">
                                                        {t("resReject.clearAllNotes")}
                                                    </Button>
                                                </Popconfirm>
                                                <Button type="primary" size="middle" loading={exportStatus != enmExportStatus.DONE} onClick={handleExportToPDFALLClick} icon={<PdfIcon />} >
                                                    {t("resReject.printAll")}
                                                </Button>
                                            </Flex>
                                        }
                                        <Flex gap="small" wrap justify="flex-end">
                                            <Button type="primary" className="btn-success" onClick={handleSendClick} icon={<CheckOutlined />} size="middle">
                                                {t("resReject.send")}
                                            </Button>
                                            <Button type="primary" className="btn-success" onClick={handleSendAndExportToPDFClick} icon={<CheckOutlined />} size="middle">
                                                {t("resReject.sendAndPrint")}
                                            </Button>
                                            <Button type="primary" danger onClick={() => props.closeCallBack && props.closeCallBack()} icon={<CloseOutlined />} size="middle">
                                                {t("resReject.close")}
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </div>
                            </Fragment>

                        }
                    </Badge.Ribbon >
                </Spin>

            }
            <SFModal
                key={"res-reject-all-note-model"}
                width={window.innerWidth * 40 / 100}
                open={openSameNoteModal}
                toggler={() => { setOpenSameNoteModal(!openSameNoteModal) }}
                body={<Input.TextArea rows={5} maxLength={500} value={allNote || ""} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleNoteChange(event.target.value, 0, true)} />}
                footer={() =>
                    <>
                        <Button type="primary" className="btn-success" onClick={handleAllNoteConfirm} size="middle">
                            {t("common.ok")}
                        </Button>
                        <Button type="primary" danger onClick={() => setOpenSameNoteModal(false)} icon={<CloseOutlined />} size="middle">
                            {t("common.close")}
                        </Button>
                    </>
                }
                title={t("resReject.note")}
            />
        </>
    )

}